import { z } from 'zod';
import { paginationQuery } from '../api.js';
/**
 * Webchat (Modul `webchat`, KON-173, siehe docs/webseite.md): Besucher einer Webseite schreiben im Chat-Fenster, das Backoffice antwortet. Die Webseite spricht über einen API-Schlüssel mit
 * dem Bereich `webchat:visitor`, jeder Besucher bekommt beim Start ein eigenes Geheimnis (Besucherschlüssel), mit dem nur er seine Unterhaltung lesen und fortsetzen kann.
 */
export const CHAT_STATUSES = ['open', 'closed'];
export const CHAT_STATUS_LABELS = { open: 'Offen', closed: 'Geschlossen' };
export const CHAT_SENDERS = ['visitor', 'staff', 'system'];
export const CHAT_LIMITS = {
    /** Zeichen je Nachricht */
    messageChars: 2000,
    /** Nachrichten je Unterhaltung, danach bleibt sie lesbar, nimmt aber nichts mehr an */
    messagesPerConversation: 500,
    /** Zeichen im Namen des Besuchers */
    nameChars: 80,
    /** Zeichen im Kommentar zur Bewertung */
    ratingCommentChars: 500,
};
/** Bewertung eines Gesprächs mit Sternen (KON-174) */
export const CHAT_RATING_LABELS = { 1: 'Sehr schlecht', 2: 'Schlecht', 3: 'In Ordnung', 4: 'Gut', 5: 'Sehr gut' };
/** Zeiträume der Auswertung in Tagen */
export const CHAT_RATING_PERIODS = ['30', '90', '365'];
const text = (max) => z.string().trim().max(max);
const optional = (max) => text(max).optional().transform((v) => (v ? v : undefined));
const message = z.string().trim().min(1, 'Bitte schreiben Sie eine Nachricht.').max(CHAT_LIMITS.messageChars, `Die Nachricht ist zu lang (höchstens ${CHAT_LIMITS.messageChars} Zeichen).`);
/** Besucher startet eine Unterhaltung. Name und E-Mail sind freiwillig. */
export const startChatInput = z
    .object({
    name: optional(CHAT_LIMITS.nameChars),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined))
        .pipe(z.email('Ungültige E-Mail-Adresse').optional()),
    message,
    /** Seite, auf der der Besucher schreibt, für den Mitarbeiter als Zusammenhang */
    pageUrl: optional(500),
    /** Rechenaufgabe, die das Chat-Fenster vor dem Start holt (GET /webchat/visitor/challenge), und ihre Lösung */
    challenge: z.string().max(200).optional(),
    solution: z.string().max(40).optional(),
    /** Versteckt im Formular: Menschen sehen es nicht und lassen es leer, Programme füllen es aus */
    website: z.string().max(200).optional(),
})
    .strict();
export const sendChatMessageInput = z.object({ body: message }).strict();
/** Besucher beendet den Chat, auf Wunsch mit Bewertung (1 bis 5 Sterne) und Kommentar. Ohne Angaben endet das Gespräch einfach. */
export const finishChatInput = z
    .object({
    rating: z.number().int('Bitte wählen Sie ganze Sterne').min(1, 'Bitte wählen Sie 1 bis 5 Sterne').max(5, 'Bitte wählen Sie 1 bis 5 Sterne').optional(),
    comment: optional(CHAT_LIMITS.ratingCommentChars),
})
    .strict()
    .refine((v) => v.comment === undefined || v.rating !== undefined, { message: 'Ein Kommentar braucht eine Bewertung', path: ['comment'] });
/**
 * Bleibt eine Nachricht des Besuchers so lange ohne Antwort, bietet das Chat-Fenster an, das Anliegen per E-Mail zu senden. Der Server verlangt dieselbe Zeit (mit kleiner Toleranz),
 * damit sich der Weg nicht beliebig früh nutzen lässt.
 */
export const CHAT_EMAIL_OFFER_MINUTES = 10;
/** Der Text der Nachricht im Verlauf, wenn das Anliegen per E-Mail weitergeleitet wurde. Das Chat-Fenster erkennt daran, dass es erledigt ist. */
export const CHAT_EMAIL_SENT_NOTE = 'Anliegen per E-Mail weitergeleitet. Die Antwort kommt per E-Mail.';
/** Der Besucher schickt sein Anliegen per E-Mail an das Unternehmen und nennt dafür seine Adresse */
export const visitorEmailInput = z
    .object({
    email: z.string().trim().toLowerCase().min(1, 'Bitte geben Sie Ihre E-Mail-Adresse an.').max(200).pipe(z.email('Bitte geben Sie eine gültige E-Mail-Adresse an.')),
})
    .strict();
export const listChatMessagesQuery = z.object({ after: z.coerce.number().int().min(0).default(0) });
export const listChatsQuery = paginationQuery.extend({
    status: z.enum(CHAT_STATUSES).optional(),
    /** Nur Unterhaltungen, die auf eine Antwort warten */
    waiting: z.enum(['1']).optional(),
    mine: z.enum(['1']).optional(),
    /** Sucht im Namen, in der E-Mail-Adresse und im Text aller Nachrichten der Unterhaltung */
    q: z.string().trim().max(100).optional(),
    /** Letzte Nachricht am oder nach diesem Tag und am oder vor diesem Tag (Datum, deutsche Zeit) */
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** Sterne der Bewertung, oder „none“ für Unterhaltungen ohne Bewertung */
    rating: z.enum(['1', '2', '3', '4', '5', 'none']).optional(),
    channel: z.enum(['web', 'facebook']).optional(),
    /** „none“: Unterhaltungen, die noch niemand betreut */
    assigned: z.enum(['none']).optional(),
});
export const updateChatInput = z
    .object({
    status: z.enum(CHAT_STATUSES).optional(),
    assignedUserId: z.uuid('Ungültiger Benutzer').nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, 'Keine Änderung angegeben');
export const chatRatingsQuery = z.object({ period: z.enum(CHAT_RATING_PERIODS).default('30') });
//# sourceMappingURL=webchat.js.map